export const codeSnippet = {
  code1: `Primary Key = Partition Key only
Example: UserID (must be unique)`,
  code2: `Primary Key = Partition Key + Sort Key
Example: UserID (partition) + Timestamp (sort)`,
  code3: `<!-- AWS SDK for DynamoDB -->
<dependency>
    <groupId>software.amazon.awssdk</groupId>
    <artifactId>dynamodb-enhanced</artifactId>
    <version>version</version>
</dependency>

<dependency>
    <groupId>software.amazon.awssdk</groupId>
    <artifactId>dynamodb</artifactId>
    <version>version</version>
</dependency>`,
  code4: `spring:
  application:
    name: dynamodb-sample
  profiles:
    active: local

# Server Configuration
server:
  port: 8080
  servlet:
    context-path: /api

# Management/Actuator Configuration
management:
  endpoints:
    web:
      exposure:
        include: health,info,metrics,env
  endpoint:
    health:
      show-details: always

# Logging Configuration
logging:
  level:
    com.example.dynamodb: DEBUG
    software.amazon.awssdk: INFO
  pattern:
    console: "%d{HH:mm:ss.SSS} [%thread] %-5level %logger{36} - %msg%n"

# Application Configuration
app:
  name: "DynamoDB Sample Application"
  version: "1.0.0"

### application-local.yml

spring:
  config:
    activate:
      on-profile: local

# AWS Local Configuration
aws:
  region: us-east-1
  dynamodb:
    endpoint: http://localhost:8000
  credentials:
    access-key: dummy
    secret-key: dummy

# Logging for local development
logging:
  level:
    software.amazon.awssdk: DEBUG
    com.example.dynamodb: DEBUG

### application-dev.yml
spring:
  config:
    activate:
      on-profile: dev

# AWS Development Configuration
aws:
  region: dev
  credentials:
    access-key: access-key
    secret-key: secret-key

# Development specific settings
logging:
  level:
    com.example.dynamodb: DEBUG
    software.amazon.awssdk: INFO

### application-prod.yml
spring:
  config:
    activate:
      on-profile: prod

# AWS Production Configuration
aws:
  region: dev
  # Use IAM roles in production, no explicit credentials

# Production logging
logging:
  level:
    com.example.dynamodb: INFO
    software.amazon.awssdk: WARN
    org.springframework: WARN
`,
  code5: `@Configuration
@Slf4j
public class DynamoDbConfig {

    @Value("$aws.region")
    private String region;

    @Value("$aws.dynamodb.endpoint:#{null}}")
    private String endpoint;

    @Value("$aws.credentials.access-key:#{null}}")
    private String accessKey;

    @Value("$aws.credentials.secret-key:#{null}}")
    private String secretKey;

    @Bean
    public AwsCredentialsProvider awsCredentialsProvider() {
        if (accessKey != null && secretKey != null) {
            log.info("Using static AWS credentials");
            return StaticCredentialsProvider.create(
                    AwsBasicCredentials.create(accessKey, secretKey));
        }
        
        log.info("Using default AWS credentials provider chain");
        return DefaultCredentialsProvider.create();
    }

    @Bean
    public DynamoDbClient dynamoDbClient(AwsCredentialsProvider credentialsProvider) {
        DynamoDbClient.Builder builder = DynamoDbClient.builder()
                .region(Region.of(region))
                .credentialsProvider(credentialsProvider)
                .overrideConfiguration(ClientOverrideConfiguration.builder()
                        .apiCallTimeout(Duration.ofSeconds(30))
                        .apiCallAttemptTimeout(Duration.ofSeconds(10))
                        .build());

        if (endpoint != null) {
            log.info("Using DynamoDB endpoint: {}", endpoint);
            builder.endpointOverride(URI.create(endpoint));
        }

        return builder.build();
    }

    @Bean
    public DynamoDbEnhancedClient dynamoDbEnhancedClient(DynamoDbClient dynamoDbClient) {
        return DynamoDbEnhancedClient.builder()
                .dynamoDbClient(dynamoDbClient)
                .build();
    }
}`,
  code6: `import lombok.AllArgsConstructor;
import lombok.Builder;
import lombok.Data;
import lombok.NoArgsConstructor;
import software.amazon.awssdk.enhanced.dynamodb.mapper.annotations.*;

import java.time.Instant;
import java.util.List;
import java.util.Map;

@Data
@Builder
@NoArgsConstructor
@AllArgsConstructor
@DynamoDbBean
public class User {

    @DynamoDbPartitionKey
    @DynamoDbAttribute("userId")
    private String userId;

    @DynamoDbAttribute("email")
    @DynamoDbSecondaryPartitionKey(indexNames = "EmailIndex")
    private String email;

    @DynamoDbAttribute("firstName")
    private String firstName;

    @DynamoDbAttribute("lastName")
    private String lastName;

    @DynamoDbAttribute("status")
    @DynamoDbSecondaryPartitionKey(indexNames = "StatusIndex")
    private String status;

    @DynamoDbAttribute("createdAt")
    @DynamoDbSecondarySortKey(indexNames = {"StatusIndex", "EmailIndex"})
    private Instant createdAt;

    @DynamoDbAttribute("updatedAt")
    private Instant updatedAt;

    @DynamoDbAttribute("roles")
    private List<String> roles;

    @DynamoDbAttribute("preferences")
    private Map<String, String> preferences;

    @DynamoDbAttribute("isActive")
    private Boolean isActive;
}`,
  code7: `import com.example.dynamodb.model.User;
import lombok.RequiredArgsConstructor;
import lombok.extern.slf4j.Slf4j;
import org.springframework.stereotype.Repository;
import software.amazon.awssdk.enhanced.dynamodb.DynamoDbEnhancedClient;
import software.amazon.awssdk.enhanced.dynamodb.DynamoDbIndex;
import software.amazon.awssdk.enhanced.dynamodb.DynamoDbTable;
import software.amazon.awssdk.enhanced.dynamodb.Key;
import software.amazon.awssdk.enhanced.dynamodb.model.*;

import java.time.Instant;
import java.util.List;
import java.util.Optional;
import java.util.stream.Collectors;

@Repository
@RequiredArgsConstructor
@Slf4j
public class UserRepository {

    private final DynamoDbEnhancedClient enhancedClient;
    private static final String TABLE_NAME = "Users";

    private DynamoDbTable<User> getTable() {
        return enhancedClient.table(TABLE_NAME, 
            software.amazon.awssdk.enhanced.dynamodb.TableSchema.fromBean(User.class));
    }

    public User save(User user) {
        if (user.getCreatedAt() == null) {
            user.setCreatedAt(Instant.now());
        }
        user.setUpdatedAt(Instant.now());
        
        getTable().putItem(user);
        log.info("User saved: {}", user.getUserId());
        return user;
    }

    public Optional<User> findById(String userId) {
        Key key = Key.builder().partitionValue(userId).build();
        User user = getTable().getItem(key);
        return Optional.ofNullable(user);
    }

    public Optional<User> findByEmail(String email) {
        DynamoDbIndex<User> emailIndex = getTable().index("EmailIndex");
        
        QueryEnhancedRequest request = QueryEnhancedRequest.builder()
                .queryConditional(QueryConditional.keyEqualTo(Key.builder()
                        .partitionValue(email)
                        .build()))
                .limit(1)
                .build();

        return emailIndex.query(request)
                .stream()
                .flatMap(page -> page.items().stream())
                .findFirst();
    }

    public List<User> findByStatus(String status) {
        DynamoDbIndex<User> statusIndex = getTable().index("StatusIndex");
        
        QueryEnhancedRequest request = QueryEnhancedRequest.builder()
                .queryConditional(QueryConditional.keyEqualTo(Key.builder()
                        .partitionValue(status)
                        .build()))
                .build();

        return statusIndex.query(request)
                .stream()
                .flatMap(page -> page.items().stream())
                .collect(Collectors.toList());
    }

    public void deleteById(String userId) {
        Key key = Key.builder().partitionValue(userId).build();
        getTable().deleteItem(key);
        log.info("User deleted: {}", userId);
    }

    public List<User> findAll() {
        return getTable().scan()
                .stream()
                .flatMap(page -> page.items().stream())
                .collect(Collectors.toList());
    }
}`,
  code8: ``,
  code9: ``,
  code10: ``,
  code11: ``,
  code12: ``,
  code13: ``,
}

